import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";

import { Overview } from "@/components/marketing/Overview";
import { PostRuns } from "@/components/marketing/PostRuns";
import { CarouselReview } from "@/components/marketing/CarouselReview";
import { ContentPanel } from "@/components/marketing/ContentPanel";
import { PendingApproval } from "@/components/marketing/PendingApproval";
import { api } from "@/lib/api";
import { isAuthenticated } from "@/lib/pb";

export const Route = createFileRoute("/marketing/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: MarketingPage,
});

function MarketingPage() {
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [picked, setPicked] = useState<{ slug: string; platform: string } | null>(null);
  // El slug del carrusel abierto en el riel. Va en la página y no en el panel
  // porque el riel se come la pantalla entera: una slide de 1080x1350 dentro
  // de una columna de 420px no es una revisión, es una miniatura.
  const [carrusel, setCarrusel] = useState<string | null>(null);

  // Los archivos los escribe un workflow, no un usuario: el backend cachea 15
  // minutos y acá no hace falta refetch periódico.
  const q = useQuery({
    queryKey: ["marketing", week ?? "latest"],
    queryFn: () => api.marketing.read(week),
  });

  // Lo pendiente se consulta aparte del índice: existe antes de que el agente
  // haya escrito nada, así que no aparece entre los posts con salida.
  const pendingQuery = useQuery({
    queryKey: ["marketing-pending"],
    queryFn: () => api.marketing.pending(),
  });

  const contentQuery = useQuery({
    queryKey: ["marketing-content", picked?.slug, picked?.platform],
    queryFn: () => api.marketing.content(picked!.slug, picked!.platform),
    enabled: picked !== null,
  });

  if (q.isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="surface h-32 animate-pulse" />
        <div className="surface h-64 animate-pulse" />
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="p-6">
        <div className="surface p-6">
          <p className="path-chip">marketing</p>
          <p className="mt-2 text-sm text-danger">
            No se pudo leer la salida del agente: {String(q.error)}
          </p>
        </div>
      </div>
    );
  }

  const data = q.data!;

  // El agente puede no haber corrido nunca. Eso es un estado, no un error, y
  // la pantalla vacía sin explicación es lo que hace pensar que algo falló.
  const pendientes = pendingQuery.data?.pendientes ?? [];

  if (!data.generated || data.posts.length === 0) {
    return (
      <div className="p-6 space-y-5">
        {pendientes.map((t) => <PendingApproval key={t.slug} triage={t} />)}
        <div className="surface p-10 text-center space-y-3">
          <p className="path-chip inline-block">marketing</p>
          <h2 className="text-xl font-semibold">El agente social todavía no corrió</h2>
          <p className="text-sm text-muted max-w-md mx-auto leading-relaxed">
            Cuando corra, acá van a aparecer las decisiones por red de cada post:
            qué se publica, qué quedó de suplente y qué se saltó sin gastar nada.
          </p>
          <p className="font-mono text-xs text-muted pt-2">
            gh workflow run social-agent.yml -f slug=&lt;slug&gt;
          </p>
        </div>
      </div>
    );
  }

  const { summary } = data;

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="path-chip">marketing</p>
          <h1 className="text-2xl font-semibold mt-1">Agente social</h1>
        </div>
      </header>

      {pendientes.map((t) => <PendingApproval key={t.slug} triage={t} />)}

      <Overview
        posts={data.posts}
        week={data.week}
        summary={summary}
        pendientes={pendientes.length}
      />

      {/* El selector de semana vive con la ocupación, que es lo único que
          cambia al cambiarla. */}
      {data.week.known_weeks.length > 1 && (
        <div className="flex justify-end -mt-2">
          <select
            value={data.week.week ?? ""}
            onChange={(e) => setWeek(e.target.value || undefined)}
            className="bg-bg-elev/60 border border-border rounded px-2 py-1
                       font-mono text-xs text-text focus:border-primary/60 focus:outline-none"
          >
            {data.week.known_weeks.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-5">
        <PostRuns
          posts={data.posts}
          picked={picked}
          onPick={(slug, platform) => setPicked({ slug, platform })}
        />
        <ContentPanel
          data={contentQuery.data ?? null}
          loading={contentQuery.isFetching}
          empty={picked === null}
          onVerCarrusel={setCarrusel}
        />
      </div>

      <AnimatePresence>
        {carrusel && (
          <CarouselReview slug={carrusel} onClose={() => setCarrusel(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
